import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { adminLogin } from "../lib/api";
import { adminAuth } from "../lib/identity";
import { useAdminAuth } from "../lib/useIdentity";
import { ShieldCheck } from "lucide-react";
import { MaliStripe } from "../components/MaliStripe";

export default function AdminLoginPage() {
  const [loggedIn] = useAdminAuth();
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Clear the error inline when the user edits the password — semantically
  // identical to the previous `useEffect(() => setErr(""), [pwd])` pattern
  // but avoids the codebase-wide `react-hooks/set-state-in-effect` false
  // positive that we kept getting flagged on the effect form.
  const onPwdChange = (e) => {
    setPwd(e.target.value);
    if (err) setErr("");
  };

  if (loggedIn) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await adminLogin(pwd);
      // Cookies (rr_admin_token + rr_admin_csrf) were just set by the
      // backend. We only need to flip the local "is logged in" flag.
      adminAuth.markLoggedIn();
    } catch (e) {
      // Diagnose three failure shapes so the operator sees the actual
      // cause instead of a vague "Connexion impossible":
      //   1. Backend returned 4xx/5xx with a French detail → show it.
      //   2. Network / CORS / preflight error → show a hint about
      //      the env config the deploy needs.
      //   3. Truly unknown → fall back to the generic string.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (detail) {
        setErr(detail);
      } else if (!e?.response) {
        // axios sets `e.response` only when the server replied. Absent
        // = network layer rejection (DNS, mixed-content, CORS preflight).
        setErr("Erreur réseau — vérifiez la configuration CORS_ORIGINS du backend.");
      } else {
        setErr(`Connexion impossible (HTTP ${status || "?"})`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col" data-testid="admin-login-page">
      <MaliStripe />
      <div className="bg-rescue-ink text-white px-5 py-6">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-90 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-mali-yellow" /> Espace administrateur
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-none mt-2">CONNEXION</h1>
        <p className="text-sm opacity-90 mt-3">Réservé aux modérateurs autorisés. Saisissez le mot de passe administrateur.</p>
      </div>
      <form onSubmit={submit} className="mx-5 mt-5 mb-4 border-2 border-rescue-ink bg-white p-4 space-y-3">
        <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute" htmlFor="admin-pwd">
          Mot de passe administrateur
        </label>
        <input
          id="admin-pwd"
          autoFocus
          type="password"
          data-testid="admin-password-input"
          value={pwd}
          onChange={onPwdChange}
          placeholder="••••••••"
          className="w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-3 text-sm font-mono"
        />
        {err && (
          <div className="text-xs text-rescue-red font-bold uppercase tracking-[0.15em]" data-testid="admin-login-error">{err}</div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-login-btn"
          className="w-full bg-rescue-red text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-rescue-ink transition-colors disabled:opacity-60"
        >
          {submitting ? "Connexion…" : "Se connecter"}
        </button>
      </form>
      <div className="mx-5 mb-8 border-t-2 border-rescue-line pt-3 text-center">
        <Link
          to="/admin/agency/login"
          data-testid="link-agency-login"
          className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute hover:text-rescue-ink"
        >
          Espace agences →
        </Link>
      </div>
    </div>
  );
}
